import { Box, Chip, IconButton, Tooltip } from "@mui/material"
import { InfoOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { ActionsComponent } from "../ActionsComponent"

import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { Location } from "@/graphql/codegen/graphql"
import type {
  LocationConfirmationDialogs,
  LocationMenuDialogs,
} from "@/screens/Companies/edit/components/locations/types/dialogs"
import { AddressFormat } from "@/types/common"
import { createStatusMap } from "@/utils/statusMap"
import { formatLocationAddress } from "@/utils/utils"

const locationStatusMap = createStatusMap<boolean, "secondary" | "success">([
  [true, "success"],
  [false, "secondary"],
])

export const useLocationTableColumns = (
  setOpenedDialog: (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => void,
  setActiveEditLocation: (location: Location) => void,
  reexecuteLocationListQuery: () => void,
  isCustomerCompanyType: boolean
): ColumnDef<Location, unknown>[] => {
  return useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Location Name",
        size: 240,
        cell: ({ row, cell }: CellContext<Location, unknown>) => {
          const tooltipTitle = formatLocationAddress(row.original, AddressFormat.Long)
          return (
            <Box className='flex items-center space-x-4'>
              <StatusIndicator
                data-testid={`${cell.id}-${(cell.getValue() as Location).locationId}`}
                status={row.original.isActive}
                map={locationStatusMap}
              />
              <Box className='flex items-center space-x-2'>
                {tooltipTitle ? (
                  <Tooltip title={tooltipTitle}>
                    <Box className='flex items-center space-x-1'>
                      <span>{cell.getValue() as string}</span>
                      <InfoOutlined fontSize='small' />
                    </Box>
                  </Tooltip>
                ) : (
                  <span>{cell.getValue() as string}</span>
                )}
              </Box>
            </Box>
          )
        },
      },
      {
        accessorKey: "description",
        header: "Location Description",
        enableSorting: false,
      },
      {
        accessorKey: "address1",
        header: "Address",
        cell: ({ row }: CellContext<Location, unknown>) =>
          formatLocationAddress(row.original, AddressFormat.AddressOnly),
        size: 288,
      },

      ...(isCustomerCompanyType
        ? [
            {
              accessorKey: "salesman.name",
              header: "Salesperson",
              enableSorting: false,
              cell: ({ row }: CellContext<Location, unknown>) => {
                const { salesman } = row.original
                return salesman ? <Chip label={`${salesman.name}`} className='rounded-sm bg-primary-100' /> : ""
              },
            },
          ]
        : [
            {
              accessorKey: "accountManager",
              header: "Account Manager",
              enableSorting: false,
              cell: ({ row }: CellContext<Location, unknown>) => {
                const { accountManager } = row.original
                return accountManager ? <Chip label={accountManager.name} className='rounded-sm bg-primary-100' /> : ""
              },
            },
          ]),

      {
        accessorKey: "notes",
        header: "",
        enableSorting: false,
        cell: () => "",
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }: CellContext<Location, unknown>) => {
          const handleOpen = (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => {
            if (!dialog) return

            setActiveEditLocation(row.original)
            setOpenedDialog(dialog)
          }
          return (
            <ActionsComponent
              row={row}
              setOpenedDialog={handleOpen}
              refetchAfterUpdate={reexecuteLocationListQuery}
              isCustomerCompanyType={isCustomerCompanyType}
            />
          )
        },
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: "expander",
        enableSorting: false,
        header: "",
        cell: ({ row }: CellContext<Location, unknown>) => (
          <IconButton onClick={row.getToggleExpandedHandler()} className='p-1'>
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        ),
        size: 50,
      },
    ],
    [reexecuteLocationListQuery, setActiveEditLocation, setOpenedDialog, isCustomerCompanyType]
  )
}
